import { useCallback, useEffect, useRef, useState } from 'react';
import { NotSignedInError, useApiFetch } from './useApiFetch';

/**
 * Sender send-result hook (PRO-0210 / UC-EP6-002). Polls
 * `GET /sends/:sendId/result` for COUNTS ONLY (queued / sending / sent / failed)
 * after an immediate send — never names, addresses or body (SEC-8). The dispatch
 * worker sends asynchronously, so the hook polls a few times until nothing is
 * in-flight, then stops (bounded — no indefinite polling).
 */
export interface SendCounts {
  readonly QUEUED: number;
  readonly SENDING: number;
  readonly SENT: number;
  readonly FAILED: number;
}

export type SendLifecycleState = 'IN_PROGRESS' | 'COMPLETE';

interface SendResultResponse {
  readonly sendId: string;
  readonly counts: SendCounts;
  readonly state?: SendLifecycleState;
  readonly progress?: number;
  readonly excluded?: number;
  readonly undeliverable?: number;
  readonly smsOptOutSuppressed?: number;
  readonly noPreferredMobile?: number;
}

export interface SendResultState {
  readonly counts: SendCounts | null;
  readonly state: SendLifecycleState | null;
  readonly progress: number | null;
  readonly excluded: number | null;
  readonly undeliverable: number | null;
  readonly smsOptOutSuppressed: number | null;
  readonly noPreferredMobile: number | null;
  readonly busy: boolean;
  readonly error: string | null;
}

const POLL_INTERVAL_MS = 4000;
const MAX_POLLS = 15;

function inFlight(counts: SendCounts | null): boolean {
  return counts === null || counts.QUEUED > 0 || counts.SENDING > 0;
}

export function useSendResult(sendId: string | null): SendResultState {
  const apiFetch = useApiFetch();
  const [state, setState] = useState<SendResultState>({
    counts: null,
    state: null,
    progress: null,
    excluded: null,
    undeliverable: null,
    smsOptOutSuppressed: null,
    noPreferredMobile: null,
    busy: false,
    error: null,
  });
  const pollsRef = useRef(0);

  const fetchOnce = useCallback(async (): Promise<SendCounts | null> => {
    if (!sendId) {
      return null;
    }
    try {
      const response = await apiFetch(`/sends/${sendId}/result`);
      if (!response) {
        return null;
      }
      if (response.status === 200) {
        const data = (await response.json()) as SendResultResponse;
        setState({
          counts: data.counts,
          state: data.state ?? null,
          progress: data.progress ?? null,
          excluded: data.excluded ?? null,
          undeliverable: data.undeliverable ?? null,
          smsOptOutSuppressed: data.smsOptOutSuppressed ?? null,
          noPreferredMobile: data.noPreferredMobile ?? null,
          busy: false,
          error: null,
        });
        return data.counts;
      }
      setState((prev) => ({
        ...prev,
        busy: false,
        error:
          response.status === 403
            ? 'You do not have permission to view this send result.'
            : `Could not load the send result (HTTP ${response.status}).`,
      }));
      return null;
    } catch (error) {
      setState((prev) => ({
        ...prev,
        busy: false,
        error:
          error instanceof NotSignedInError
            ? error.message
            : 'Could not load the send result just now.',
      }));
      return null;
    }
  }, [apiFetch, sendId]);

  useEffect(() => {
    if (!sendId) {
      setState({ counts: null, state: null, progress: null, excluded: null, undeliverable: null, smsOptOutSuppressed: null, noPreferredMobile: null, busy: false, error: null });
      return;
    }
    let cancelled = false;
    pollsRef.current = 0;
    setState({ counts: null, state: null, progress: null, excluded: null, undeliverable: null, smsOptOutSuppressed: null, noPreferredMobile: null, busy: true, error: null });

    const tick = async (): Promise<void> => {
      if (cancelled) {
        return;
      }
      pollsRef.current += 1;
      const counts = await fetchOnce();
      if (cancelled) {
        return;
      }
      if (inFlight(counts) && pollsRef.current < MAX_POLLS) {
        timer = window.setTimeout(() => void tick(), POLL_INTERVAL_MS);
      }
    };
    let timer = window.setTimeout(() => void tick(), 0);

    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [sendId, fetchOnce]);

  return state;
}
